import { inject } from "@angular/core";
import { RedirectCommand, ResolveFn, Router } from "@angular/router";
import { catchError, map, Observable, of, switchMap, tap } from "rxjs";

import { FreeformId, isFreeformId, isPollId, PollId } from "../../../shared/models/entity.model";
import { TenantResponse } from "../../../admin/tenants/models/tenant.model";
import { InteractivesListResponse } from "../../../interactives/models/interactives-list.model";
import { toInteractiveTypedId } from "../../../interactives/mappers/interactives-list.mapper";
import { FreeformApiService } from "../../../interactives/services/freeform.api.service";
import { InteractivesApiService } from "../../../interactives/services/interactives.api.service";
import { PollApiService } from "../../../interactives/services/poll.api.service";
import { TenantsApiService } from "../../../shared/services/tenants.api.service";
import { NavbarService } from "../navbar.service";

const EMPTY_RESPONSE: InteractivesListResponse = { items: [], page: 0, size: 0, totalItems: 0, totalPages: 0 };

// Data flows through NavbarService — this resolver fetches tenants/interactives as a side effect and redirects if needed.
export const tenantInteractivesResolver: ResolveFn<undefined> = route => {
    const interactivesService = inject(InteractivesApiService);
    const freeformApiService = inject(FreeformApiService);
    const pollApiService = inject(PollApiService);
    const tenantsService = inject(TenantsApiService);
    const router = inject(Router);
    const navbarService = inject(NavbarService);

    // activeView() reflects the previous URL (router.url at resolver time, before NavigationEnd).
    // Non-null means navigating within the interactives route tree (in-session).
    // allTenants + allInteractives both non-empty catches redirect re-runs: the first resolver run
    // fetches and populates both, then redirects; the second run reuses that data without re-fetching.
    // If allInteractives is empty (e.g. returning after a tenant-with-no-interactives state),
    // we fall through to the fresh load path so the first valid tenant is selected instead.
    const existingTenantId =
        navbarService.activeView() !== null ||
        (navbarService.allTenants().length > 0 && navbarService.allInteractives().length > 0)
            ? navbarService.tenantId()
            : "";
    const typedInteractiveId = route.firstChild?.firstChild?.url[0]?.path ?? "";
    const view = route.firstChild?.routeConfig?.path;

    const redirectTo = (typedInteractiveId?: PollId | FreeformId): RedirectCommand =>
        new RedirectCommand(
            router.createUrlTree(
                typedInteractiveId ? ["interactives", view, typedInteractiveId] : ["interactives", view]
            ),
            { replaceUrl: true }
        );

    // Fetches interactives for a tenant, updates the service, and resolves routing.
    // When a typed ID is present the child interactivesResolver validates it — no redirect here.
    // When no typed ID, redirects to the first interactive or stays for the empty-state overlay.
    const listByTenant = (tenantId: string): Observable<undefined | RedirectCommand> =>
        interactivesService.getAllInteractives(tenantId, true).pipe(
            catchError(() => of(EMPTY_RESPONSE)),
            tap(r => {
                navbarService.setSelectedTenantId(tenantId);
                navbarService.setAllInteractives(r.items);
            }),
            map((r): undefined | RedirectCommand => {
                if (typedInteractiveId || !r.items.length) return undefined;
                return redirectTo(toInteractiveTypedId(r.items[0]));
            })
        );

    // In-session navigation (e.g. tenant switch) — tenants already in service.
    if (existingTenantId) {
        // If the interactive is already in the list, no need to re-fetch.
        const existingInteractiveId = route.firstChild?.firstChild?.paramMap.get("interactiveId") ?? "";
        if (existingInteractiveId && navbarService.allInteractives().some(i => i.id === existingInteractiveId)) {
            return undefined;
        }
        return listByTenant(existingTenantId);
    }

    // Fresh load: fetch and store tenants, then resolve interactive routing.
    return tenantsService.getAuthorizedTenants().pipe(
        catchError(() => of([] as TenantResponse[])),
        tap(tenants => navbarService.setAllTenants(tenants)),
        switchMap((tenants): Observable<undefined | RedirectCommand> => {
            // Typed freeform ID — derive tenant from the interactive itself.
            if (isFreeformId(typedInteractiveId)) {
                return freeformApiService.get(typedInteractiveId).pipe(
                    switchMap(interactive => listByTenant(interactive.tenantId)),
                    catchError(() => of(redirectTo()))
                );
            }

            // Typed poll ID — derive tenant from the interactive itself.
            if (isPollId(typedInteractiveId)) {
                return pollApiService.get(typedInteractiveId).pipe(
                    switchMap(interactive => listByTenant(interactive.tenantId)),
                    catchError(() => of(redirectTo()))
                );
            }

            // No typed ID, no tenants available - show empty state.
            if (!tenants.length) {
                navbarService.setAllInteractives([]);
                return of(undefined);
            }

            // No typed ID — use the first authorized tenant.
            return listByTenant(tenants[0].id);
        })
    );
};
